import { useState, useMemo, useRef, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Edit,
  Trash2,
  ChevronDown,
  MoreHorizontal,
  Download,
  LayoutGrid,
  List,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { useInventory, GanttOrder, GanttTask } from '@/context/InventoryContext';
import { SaveIndicator } from '@/components/ui/save-indicator';

// Initial data from GANT.xlsx
const INITIAL_GANTT_DATA: GanttOrder[] = [
  {
    id: 'order-0',
    orderNumber: '450102222',
    productName: 'ThermaLink 6 PC',
    tasks: [
      { id: 'task-order-0-1', taskNumber: 1, name: 'raw material SMC', progress: 100, startDate: '2025-12-27', endDate: '2025-12-29', daysOfWork: 0 },
      { id: 'task-order-0-2', taskNumber: 2, name: 'raw material POS', progress: 100, startDate: '2025-12-27', endDate: '2025-12-29', daysOfWork: 30 },
      { id: 'task-order-0-3', taskNumber: 3, name: 'POS WORK', progress: 60, startDate: '2025-12-29', endDate: '2026-01-28', daysOfWork: 30 },
    ],
  },
  {
    id: 'order-1',
    orderNumber: '450115430',
    productName: 'ThermaLink 12 PC',
    tasks: [
      { id: 'task-order-1-1', taskNumber: 1, name: 'raw material SMC', progress: 100, startDate: '2025-12-17', endDate: '2025-01-20', daysOfWork: 50 },
      { id: 'task-order-1-2', taskNumber: 2, name: 'raw material POS', progress: 100, startDate: '2025-12-17', endDate: '2026-01-16', daysOfWork: 30 },
      { id: 'task-order-1-3', taskNumber: 3, name: 'POS WORK', progress: 0, startDate: '2025-01-20', endDate: '2025-02-10', daysOfWork: 21 },
    ],
  },
  {
    id: 'order-2',
    orderNumber: '450116359',
    productName: 'ThermaLink 6 PC',
    tasks: [
      { id: 'task-order-2-1', taskNumber: 1, name: 'raw material SMC', progress: 50, startDate: '2025-12-22', endDate: '2026-02-10', daysOfWork: 50 },
      { id: 'task-order-2-2', taskNumber: 2, name: 'raw material POS', progress: 90, startDate: '2025-12-22', endDate: '2026-01-21', daysOfWork: 30 },
      { id: 'task-order-2-3', taskNumber: 3, name: 'POS WORK', progress: 0, startDate: '2026-02-10', endDate: '2026-03-03', daysOfWork: 21 },
    ],
  },
  {
    id: 'order-3',
    orderNumber: '450115430',
    productName: 'ThermaLink 12 PC',
    tasks: [
      { id: 'task-order-3-1', taskNumber: 1, name: 'raw material SMC', progress: 75, startDate: '2025-12-17', endDate: '2026-02-05', daysOfWork: 50 },
      { id: 'task-order-3-2', taskNumber: 2, name: 'raw material POS', progress: 100, startDate: '2025-12-17', endDate: '2026-01-16', daysOfWork: 30 },
      { id: 'task-order-3-3', taskNumber: 3, name: 'POS WORK', progress: 0, startDate: '2026-02-05', endDate: '2026-02-26', daysOfWork: 21 },
    ],
  },
  {
    id: 'order-4',
    orderNumber: '450118078',
    productName: 'ThermaLink 10 PC',
    tasks: [
      { id: 'task-order-4-1', taskNumber: 1, name: 'raw material SMC', progress: 50, startDate: '2026-01-04', endDate: '2026-02-23', daysOfWork: 50 },
      { id: 'task-order-4-2', taskNumber: 2, name: 'raw material POS', progress: 100, startDate: '2026-01-04', endDate: '2026-02-03', daysOfWork: 30 },
      { id: 'task-order-4-3', taskNumber: 3, name: 'POS WORK', progress: 0, startDate: '2026-02-23', endDate: '2026-03-16', daysOfWork: 21 },
    ],
  },
];

// Day width in pixels
const DAY_WIDTH = 32;
const ROW_HEIGHT = 40;

// Generate dates for timeline
function generateDates(startDate: Date, days: number): Date[] {
  const dates: Date[] = [];
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    dates.push(date);
  }
  return dates;
}

// Group dates by week
function groupDatesByWeek(dates: Date[]): { weekStart: Date; dates: Date[] }[] {
  const weeks: { weekStart: Date; dates: Date[] }[] = [];
  let currentWeek: Date[] = [];
  let currentWeekStart: Date | null = null;

  dates.forEach((date, index) => {
    if (date.getDay() === 0 || index === 0) {
      if (currentWeek.length > 0 && currentWeekStart) {
        weeks.push({ weekStart: currentWeekStart, dates: currentWeek });
      }
      currentWeek = [date];
      currentWeekStart = date;
    } else {
      currentWeek.push(date);
    }
  });

  if (currentWeek.length > 0 && currentWeekStart) {
    weeks.push({ weekStart: currentWeekStart, dates: currentWeek });
  }

  return weeks;
}

// Calculate days between two dates
function daysBetween(start: Date, end: Date): number {
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

// Format date for input
function formatDateForInput(date: string): string {
  return date.split('T')[0];
}

export function GanttPage() {
  const { ganttOrders, setGanttOrders, saveStatus } = useInventory();

  // View state
  const [viewStartDate, setViewStartDate] = useState(() => {
    const today = new Date();
    today.setDate(today.getDate() - 7);
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [viewDays, setViewDays] = useState(90);
  const [collapsedOrders, setCollapsedOrders] = useState<Set<string>>(new Set());
  const [showTimeline, setShowTimeline] = useState(false);

  // Dialog state
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<GanttOrder | null>(null);
  const [editingTask, setEditingTask] = useState<{ orderId: string | number; task: GanttTask | null }>({ orderId: '', task: null });

  // Form state
  const [orderForm, setOrderForm] = useState({ orderNumber: '', productName: '' });
  const [taskForm, setTaskForm] = useState({
    name: '',
    progress: 0,
    startDate: '',
    endDate: '',
    daysOfWork: 0,
  });

  // Refs for synchronized scrolling
  const timelineRef = useRef<HTMLDivElement>(null);
  const taskListRef = useRef<HTMLDivElement>(null);

  // Initialize with data from GANT.xlsx if empty
  useEffect(() => {
    if (ganttOrders.length === 0) {
      setGanttOrders(INITIAL_GANTT_DATA);
    }
  }, []);

  // Generate timeline dates
  const timelineDates = useMemo(() => generateDates(viewStartDate, viewDays), [viewStartDate, viewDays]);
  const weekGroups = useMemo(() => groupDatesByWeek(timelineDates), [timelineDates]);

  // Today's date
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Today's position in timeline
  const todayPosition = useMemo(() => {
    const days = daysBetween(viewStartDate, today);
    return days >= 0 && days < viewDays ? days * DAY_WIDTH : -1;
  }, [viewStartDate, today, viewDays]);

  // Sync scrolling between panels
  const handleTimelineScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (taskListRef.current) {
      taskListRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const handleTaskListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  // Navigation
  const navigatePrev = () => {
    const newStart = new Date(viewStartDate);
    newStart.setDate(newStart.getDate() - 30);
    setViewStartDate(newStart);
  };

  const navigateNext = () => {
    const newStart = new Date(viewStartDate);
    newStart.setDate(newStart.getDate() + 30);
    setViewStartDate(newStart);
  };

  const goToToday = () => {
    const newStart = new Date();
    newStart.setDate(newStart.getDate() - 7);
    newStart.setHours(0, 0, 0, 0);
    setViewStartDate(newStart);
  };

  // Toggle order collapse
  const toggleOrderCollapse = (orderId: string | number) => {
    const id = String(orderId);
    setCollapsedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Order CRUD
  const handleAddOrder = () => {
    setEditingOrder(null);
    setOrderForm({ orderNumber: '', productName: '' });
    setOrderDialogOpen(true);
  };

  const handleEditOrder = (order: GanttOrder) => {
    setEditingOrder(order);
    setOrderForm({ orderNumber: order.orderNumber, productName: order.productName });
    setOrderDialogOpen(true);
  };

  const handleDeleteOrder = (orderId: string | number) => {
    setGanttOrders(prev => prev.filter(o => String(o.id) !== String(orderId)));
  };

  const handleOrderSubmit = () => {
    if (!orderForm.orderNumber || !orderForm.productName) return;

    if (editingOrder) {
      setGanttOrders(prev => prev.map(o =>
        String(o.id) === String(editingOrder.id)
          ? { ...o, orderNumber: orderForm.orderNumber, productName: orderForm.productName }
          : o
      ));
    } else {
      const newOrder: GanttOrder = {
        id: Date.now().toString(),
        orderNumber: orderForm.orderNumber,
        productName: orderForm.productName,
        tasks: [],
      };
      setGanttOrders(prev => [...prev, newOrder]);
    }
    setOrderDialogOpen(false);
  };

  // Task CRUD
  const handleAddTask = (orderId: string | number) => {
    setEditingTask({ orderId, task: null });
    const defaultStart = new Date();
    const defaultEnd = new Date();
    defaultEnd.setDate(defaultEnd.getDate() + 7);
    setTaskForm({
      name: '',
      progress: 0,
      startDate: defaultStart.toISOString().split('T')[0],
      endDate: defaultEnd.toISOString().split('T')[0],
      daysOfWork: 7,
    });
    setTaskDialogOpen(true);
  };

  const handleEditTask = (orderId: string | number, task: GanttTask) => {
    setEditingTask({ orderId, task });
    setTaskForm({
      name: task.name,
      progress: task.progress,
      startDate: formatDateForInput(task.startDate),
      endDate: formatDateForInput(task.endDate),
      daysOfWork: task.daysOfWork,
    });
    setTaskDialogOpen(true);
  };

  const handleDeleteTask = (orderId: string | number, taskId: string | number) => {
    setGanttOrders(prev => prev.map(o =>
      String(o.id) === String(orderId)
        ? { ...o, tasks: o.tasks.filter(t => String(t.id) !== String(taskId)) }
        : o
    ));
  };

  const handleTaskSubmit = () => {
    if (!taskForm.name || !taskForm.startDate || !taskForm.endDate) return;

    const taskData: GanttTask = {
      id: editingTask.task?.id || Date.now().toString(),
      taskNumber: editingTask.task?.taskNumber || 0,
      name: taskForm.name,
      progress: taskForm.progress,
      startDate: taskForm.startDate,
      endDate: taskForm.endDate,
      daysOfWork: taskForm.daysOfWork,
    };

    setGanttOrders(prev => prev.map(o => {
      if (String(o.id) !== String(editingTask.orderId)) return o;

      if (editingTask.task) {
        return {
          ...o,
          tasks: o.tasks.map(t => String(t.id) === String(editingTask.task!.id) ? taskData : t),
        };
      } else {
        const maxTaskNum = o.tasks.reduce((max, t) => Math.max(max, t.taskNumber), 0);
        taskData.taskNumber = maxTaskNum + 1;
        return {
          ...o,
          tasks: [...o.tasks, taskData],
        };
      }
    }));
    setTaskDialogOpen(false);
  };

  // Calculate task bar position and width
  const getTaskBarStyle = (task: GanttTask) => {
    const start = new Date(task.startDate);
    const end = new Date(task.endDate);
    const offsetDays = daysBetween(viewStartDate, start);
    const durationDays = daysBetween(start, end) + 1;

    return {
      left: `${offsetDays * DAY_WIDTH}px`,
      width: `${durationDays * DAY_WIDTH - 4}px`,
    };
  };

  // Calculate total rows for height
  const totalRows = useMemo(() => {
    return ganttOrders.reduce((count, order) => {
      return count + 1 + (collapsedOrders.has(String(order.id)) ? 0 : order.tasks.length);
    }, 0);
  }, [ganttOrders, collapsedOrders]);

  return (
    <div className="space-y-4 h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Gantt View</h2>
            <SaveIndicator status={saveStatus} />
          </div>
          <p className="text-muted-foreground">
            Project timeline and task management
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Timeline controls - only show when timeline is visible */}
          {showTimeline && (
            <>
              <Button variant="outline" size="sm" onClick={navigatePrev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={navigateNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Select value={viewDays.toString()} onValueChange={(v) => setViewDays(parseInt(v))}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">1 Month</SelectItem>
                  <SelectItem value="60">2 Months</SelectItem>
                  <SelectItem value="90">3 Months</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          <Button
            variant={showTimeline ? 'default' : 'outline'}
            onClick={() => setShowTimeline(!showTimeline)}
          >
            {showTimeline ? (
              <>
                <List className="h-4 w-4 mr-2" />
                Hide Timeline
              </>
            ) : (
              <>
                <LayoutGrid className="h-4 w-4 mr-2" />
                Show Timeline
              </>
            )}
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleAddOrder}>
            <Plus className="h-4 w-4 mr-2" />
            Add Order
          </Button>
        </div>
      </div>

      {/* Gantt Chart */}
      <Card className="flex-1 overflow-hidden">
        <div className="flex h-full" style={{ height: 'calc(100vh - 14rem)' }}>
          {/* Left Panel - Task List */}
          <div className={cn(
            "flex-shrink-0 flex flex-col",
            showTimeline ? "w-[450px] border-r" : "w-full"
          )}>
            {/* Header */}
            <div className="flex border-b bg-muted/50 font-medium text-sm" style={{ height: '60px' }}>
              <div className="w-10 px-2 flex items-center justify-center border-r">#</div>
              <div className="flex-1 px-3 flex items-center">Task & Subject</div>
              <div className="w-16 px-2 flex items-center justify-center border-l">Progress</div>
              <div className="w-24 px-2 flex items-center justify-center border-l">Start</div>
              <div className="w-24 px-2 flex items-center justify-center border-l">End</div>
              <div className="w-12 px-2 flex items-center justify-center border-l">Days</div>
              <div className="w-10 border-l"></div>
            </div>

            {/* Task List Body */}
            <div
              ref={taskListRef}
              className="flex-1 overflow-y-auto overflow-x-hidden"
              onScroll={handleTaskListScroll}
            >
              {ganttOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mb-4" />
                  <p>No orders yet</p>
                  <Button variant="link" onClick={handleAddOrder}>Add your first order</Button>
                </div>
              ) : (
                ganttOrders.map((order) => (
                  <div key={order.id}>
                    {/* Order Header */}
                    <div
                      className="flex items-center bg-slate-100 dark:bg-slate-800 border-b cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700"
                      style={{ height: `${ROW_HEIGHT}px` }}
                    >
                      <div className="w-10 px-2 flex items-center justify-center border-r">
                        <button onClick={() => toggleOrderCollapse(order.id)}>
                          {collapsedOrders.has(String(order.id)) ? (
                            <ChevronRight className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <div className="flex-1 px-3 font-semibold text-sm truncate" onClick={() => toggleOrderCollapse(order.id)}>
                        Order #{order.orderNumber} - {order.productName}
                      </div>
                      <div className="w-10 flex items-center justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleAddTask(order.id)}>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Task
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditOrder(order)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Order
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteOrder(order.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Order
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    {/* Tasks */}
                    {!collapsedOrders.has(String(order.id)) && order.tasks.map((task) => (
                      <div
                        key={task.id}
                        className="flex items-center border-b hover:bg-muted/30 text-sm"
                        style={{ height: `${ROW_HEIGHT}px` }}
                      >
                        <div className="w-10 px-2 flex items-center justify-center border-r text-muted-foreground">
                          {task.taskNumber}
                        </div>
                        <div className="flex-1 px-3 truncate">{task.name}</div>
                        <div className="w-16 px-2 flex items-center justify-center border-l">
                          <Badge
                            variant={task.progress === 100 ? 'success' : task.progress > 0 ? 'warning' : 'secondary'}
                            className="text-xs"
                          >
                            {task.progress}%
                          </Badge>
                        </div>
                        <div className="w-24 px-2 flex items-center justify-center border-l text-xs">
                          {formatDate(task.startDate, { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="w-24 px-2 flex items-center justify-center border-l text-xs">
                          {formatDate(task.endDate, { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="w-12 px-2 flex items-center justify-center border-l text-xs">
                          {task.daysOfWork}
                        </div>
                        <div className="w-10 flex items-center justify-center border-l">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6">
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditTask(order.id, task)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Task
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDeleteTask(order.id, task.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Task
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Timeline (conditionally rendered) */}
          {showTimeline && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Timeline Header */}
            <div className="border-b bg-muted/50" style={{ height: '60px' }}>
              <div className="flex h-1/2 border-b overflow-hidden">
                {weekGroups.map((week, idx) => (
                  <div
                    key={idx}
                    className="border-r text-xs font-medium flex items-center justify-center flex-shrink-0"
                    style={{ width: `${week.dates.length * DAY_WIDTH}px` }}
                  >
                    {week.weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                ))}
              </div>
              <div className="flex h-1/2 overflow-hidden">
                {timelineDates.map((date, idx) => {
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const isToday = date.toDateString() === today.toDateString();
                  return (
                    <div
                      key={idx}
                      className={cn(
                        "border-r text-xs flex items-center justify-center font-medium flex-shrink-0",
                        isWeekend && "bg-slate-100 dark:bg-slate-800",
                        isToday && "bg-red-100 dark:bg-red-900/30 text-red-600"
                      )}
                      style={{ width: `${DAY_WIDTH}px` }}
                    >
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Timeline Body */}
            <div
              ref={timelineRef}
              className="flex-1 overflow-auto relative"
              onScroll={handleTimelineScroll}
            >
              {/* Grid background */}
              <div
                className="absolute inset-0"
                style={{ width: `${viewDays * DAY_WIDTH}px`, minHeight: `${Math.max(totalRows, 10) * ROW_HEIGHT}px` }}
              >
                {/* Weekend columns */}
                {timelineDates.map((date, idx) => {
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  if (!isWeekend) return null;
                  return (
                    <div
                      key={idx}
                      className="absolute top-0 bottom-0 bg-slate-50 dark:bg-slate-900/50"
                      style={{ left: `${idx * DAY_WIDTH}px`, width: `${DAY_WIDTH}px` }}
                    />
                  );
                })}

                {/* Today line */}
                {todayPosition >= 0 && (
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20"
                    style={{ left: `${todayPosition + DAY_WIDTH / 2}px` }}
                  />
                )}

                {/* Vertical grid lines */}
                {timelineDates.map((_, idx) => (
                  <div
                    key={idx}
                    className="absolute top-0 bottom-0 border-r border-slate-200 dark:border-slate-700"
                    style={{ left: `${(idx + 1) * DAY_WIDTH}px` }}
                  />
                ))}
              </div>

              {/* Task rows */}
              <div className="relative" style={{ width: `${viewDays * DAY_WIDTH}px` }}>
                {ganttOrders.map((order) => (
                  <div key={order.id}>
                    {/* Order header row */}
                    <div
                      className="border-b bg-slate-100/50 dark:bg-slate-800/50"
                      style={{ height: `${ROW_HEIGHT}px` }}
                    />

                    {/* Task bars */}
                    {!collapsedOrders.has(String(order.id)) && order.tasks.map((task) => (
                      <div
                        key={task.id}
                        className="relative border-b"
                        style={{ height: `${ROW_HEIGHT}px` }}
                      >
                        {/* Task bar */}
                        <div
                          className="absolute top-1.5 h-7 rounded cursor-pointer group"
                          style={getTaskBarStyle(task)}
                          onClick={() => handleEditTask(order.id, task)}
                        >
                          {/* Background bar */}
                          <div className="absolute inset-0 bg-blue-200 dark:bg-blue-900 rounded" />
                          {/* Progress bar */}
                          <div
                            className="absolute inset-y-0 left-0 bg-blue-500 rounded-l"
                            style={{
                              width: `${task.progress}%`,
                              borderRadius: task.progress === 100 ? '0.25rem' : '0.25rem 0 0 0.25rem',
                            }}
                          />
                          {/* Label */}
                          <div className="absolute inset-0 flex items-center px-2 text-xs font-medium text-blue-900 dark:text-blue-100 truncate">
                            {task.name}
                          </div>
                          {/* Hover effect */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}
        </div>
      </Card>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingOrder ? 'Edit Order' : 'Add New Order'}</DialogTitle>
            <DialogDescription>
              {editingOrder ? 'Update the order details.' : 'Create a new order to organize tasks.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Order Number *</Label>
              <Input
                placeholder="e.g., 450102222"
                value={orderForm.orderNumber}
                onChange={(e) => setOrderForm({ ...orderForm, orderNumber: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Product Name *</Label>
              <Input
                placeholder="e.g., ThermaLink 6 PC"
                value={orderForm.productName}
                onChange={(e) => setOrderForm({ ...orderForm, productName: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleOrderSubmit} disabled={!orderForm.orderNumber || !orderForm.productName}>
              {editingOrder ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTask.task ? 'Edit Task' : 'Add New Task'}</DialogTitle>
            <DialogDescription>
              {editingTask.task ? 'Update the task details.' : 'Create a new task for this order.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Task Name *</Label>
              <Input
                placeholder="e.g., raw material SMC"
                value={taskForm.name}
                onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={taskForm.startDate}
                  onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={taskForm.endDate}
                  onChange={(e) => setTaskForm({ ...taskForm, endDate: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Progress (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={taskForm.progress}
                  onChange={(e) => setTaskForm({ ...taskForm, progress: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Days of Work</Label>
                <Input
                  type="number"
                  min="0"
                  value={taskForm.daysOfWork}
                  onChange={(e) => setTaskForm({ ...taskForm, daysOfWork: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleTaskSubmit} disabled={!taskForm.name || !taskForm.startDate || !taskForm.endDate}>
              {editingTask.task ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
